/**
 * React Query hooks for Contact Log data
 * Provides cached, optimized access to contact log records
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getContactLogsByIndividual,
  updateContactLog,
  deleteContactLog,
} from '../services/contactLogService';
import { ContactLog } from '../types/contactLog';

/**
 * Hook to fetch contact logs for a specific individual
 * Automatically disabled if ID is not provided
 */
export function useContactLogs(individualId: number | string | undefined) {
  return useQuery({
    queryKey: ['contact-logs', individualId],
    queryFn: () => getContactLogsByIndividual(Number(individualId)),
    enabled: !!individualId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to update an existing contact log
 * Invalidates contact-logs queries on success
 */
export function useUpdateContactLog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ contactLogID, data }: { contactLogID: number; data: Partial<ContactLog> }) =>
      updateContactLog(contactLogID, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact-logs'] });
    },
  });
}

/**
 * Hook to delete a contact log (admin only)
 * Invalidates contact-logs queries on success
 */
export function useDeleteContactLog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (contactLogID: number) => deleteContactLog(contactLogID),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact-logs'] });
    },
  });
}
